import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';

export type OrderStatus = 'pending' | 'paid' | 'fulfilled' | 'refunded' | 'failed';

export interface OrderListItem {
  id: string;
  status: OrderStatus;
  currency: string;
  subtotalCents: number;
  serviceFeeCents: number;
  totalCents: number;
  /** Comisión de plataforma retenida por Boletra en esta orden. */
  applicationFeeCents: number;
  /** Neto recibido por la productora (totalCents − applicationFeeCents). */
  netCents: number;
  buyer: { email: string; name: string; locale: 'es' | 'en' };
  items: Array<{
    ticketTypeId: string;
    quantity: number;
    unitPriceCents: number;
    ticketSnapshot: { name: string; kind: string };
  }>;
  utm: { source: string | null; medium: string | null; campaign: string | null };
  createdAt: string;
  fulfilledAt: string | null;
  refundedAt: string | null;
}

export interface OrdersResponse {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  /** Tarifa de plataforma configurada en el backend (informativa). */
  platformFee: {
    pct: number;
    fixedCents: number;
  };
  summary: {
    currency: string;
    revenueCents: number;
    feesCents: number;
    /** Total de comisión de plataforma retenida por Boletra. */
    platformFeeCents: number;
    /** Neto que recibe la productora (revenueCents − platformFeeCents). */
    netCents: number;
    ordersCount: number;
    ticketsSold: number;
  };
  orders: OrderListItem[];
}

export interface OrdersFilters {
  status?: OrderStatus;
  q?: string;
  page?: number;
  pageSize?: number;
}

function buildQS(filters: OrdersFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useEventOrders(eventId: string | null, filters: OrdersFilters = {}) {
  return useQuery<OrdersResponse>({
    queryKey: ['event', eventId, 'orders', filters],
    queryFn: () => api<OrdersResponse>(`/events/${eventId}/orders${buildQS(filters)}`),
    enabled: Boolean(eventId),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });
}

export interface EventAnalyticsSummary {
  eventId: string;
  currency: string;
  revenueCents: number;
  ordersCount: number;
  ticketsSold: number;
  qrsByStatus: Partial<Record<'issued' | 'validated' | 'voided' | 'refunded', number>>;
}

export function useEventSummary(eventId: string | null) {
  return useQuery<EventAnalyticsSummary>({
    queryKey: ['event', eventId, 'analytics', 'summary'],
    queryFn: () => api<EventAnalyticsSummary>(`/analytics/${eventId}/summary`),
    enabled: Boolean(eventId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export interface EventChannelRow {
  source: string;
  medium: string | null;
  revenueCents: number;
  ordersCount: number;
}

export function useEventByChannel(eventId: string | null) {
  return useQuery<{ breakdown: EventChannelRow[] }>({
    queryKey: ['event', eventId, 'analytics', 'by-channel'],
    queryFn: () => api<{ breakdown: EventChannelRow[] }>(`/analytics/${eventId}/by-channel`),
    enabled: Boolean(eventId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
